import React, { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import { httpClient } from '../../funcs'
import { notifyAboutError } from '../../methods/notifyUser'
import { loadSelf } from '../../methods/loadSelf'
import { formatDateTime } from '../../methods/formatDate'
import { ListLoader } from '../Contracts/ContractList/PageLoader'
import ConfirmationPopup from '../Common/ConfirmationPopup'
import RequestClosingDocsPopup from './RequestClosingDocsPopup'
import UploadPaymentProofPopup from './UploadPaymentProofPopup'
import InfoButton from '../../components/InfoButton'
import Popup from '../../components/Popup'
import OrderInfo from './OrderInfo'
import trashIcon from '../../images/pages/tariffsV2/trash.svg'
import downloadIcon from '../../images/pages/tariffsV2/download.svg'
import { useRoute } from 'react-router5'
import { AdditionalServiceType } from '../../util/AdditionalServiceType'
import 'pages/Styles/UIKit_v2.sass'

type Props = {
    $store: any
    $commitToStore: (data: any) => void
    onLoadTariffs?: () => void
}

type ClosingDocumentStatus = 'downloaded' | 'emailed' | 'edo_requested'

type Bill = {
    _id: string
    invoice_number: number
    status: string
    banking_status: string
    price: number
    created_at: string
    order_description: string
    has_additional_services: boolean
    items: any[]
    pdf_url?: string
    payment_proof?: string
    closing_document_status?: ClosingDocumentStatus
}

let BillsList: any = function BillsList({ $store, $commitToStore, onLoadTariffs }: Props) {
    const $router = useRoute()
    const [bills, setBills] = useState<Bill[]>([])
    const [loading, setLoading] = useState(true)
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)
    const [billToDelete, setBillToDelete] = useState<string | null>(null)
    const [showUploadPopup, setShowUploadPopup] = useState(false)
    const [billToUpload, setBillToUpload] = useState<string | null>(null)
    const [showOrderDetailsPopup, setShowOrderDetailsPopup] = useState(false)
    const [selectedBillItems, setSelectedBillItems] = useState<any[]>([])
    const [showClosingDocsPopup, setShowClosingDocsPopup] = useState(false)
    const [selectedBill, setSelectedBill] = useState<Bill | null>(null)

    useEffect(() => {
        loadBills()
    }, [])

    async function loadBills() {
        try {
            setLoading(true)
            const { data } = await httpClient.get('/user-bills')
            setBills(data.bills || [])
        } catch (error) {
            notifyAboutError({ $store, $commitToStore }, error)
        } finally {
            setLoading(false)
        }
    }

    async function downloadBill(billId: string, pdfUrl: string) {
        try {
            // Создаем ссылку для скачивания
            const link = document.createElement('a')
            link.href = pdfUrl
            link.download = `bill_${billId}.pdf`
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
        } catch (error) {
            notifyAboutError({ $store, $commitToStore }, error)
        }
    }

    async function deleteBill(billId: string) {
        try {
            await httpClient.delete(`/delete-bill/${billId}`)
            setBills(prevBills => prevBills.filter(bill => bill._id !== billId))
            setShowDeleteConfirmation(false)
            setBillToDelete(null)
        } catch (error) {
            notifyAboutError({ $store, $commitToStore }, error)
        }
    }

    async function uploadPaymentProof(billId: string) {
        setBillToUpload(billId)
        setShowUploadPopup(true)
    }

    async function handleUploadSuccess(items?: any[]) {
        // Перезагружаем список счетов после успешной загрузки
        await loadBills()
        
        // Обновляем баланс пользователя после успешной загрузки платежного поручения
        await loadSelf({ $store, $commitToStore })
        
        // Обновляем тарифы после успешной загрузки платежного поручения
        if (onLoadTariffs) {
            await onLoadTariffs()
        }

        redirectToTemplateAfterPayment(items || [])
    }

    function handleUploadClose() {
        setShowUploadPopup(false)
        setBillToUpload(null)
    }

    function handleDeleteClick(billId: string) {
        setBillToDelete(billId)
        setShowDeleteConfirmation(true)
    }

    function handleShowOrderDetails(items: any[]) {
        setSelectedBillItems(items)
        setShowOrderDetailsPopup(true)
    }

    function redirectToTemplateAfterPayment(items: any[]) {
        const aiItem = items.find(item =>
            item.type === AdditionalServiceType.AI_TEMPLATE_CONFIGURATION && item.template_id
        )
        if (!aiItem?.template_id) return

        $router.router.navigate(
            "new-contract",
            {
                act: "new",
                templateId: aiItem.template_id,
                open_ai_chat: "1",
                ai_chat_greeting: "1"
            },
            { reload: false }
        )
    }

    function handleRequestClosingDocs(bill: Bill) {
        setSelectedBill(bill)
        setShowClosingDocsPopup(true)
    }

    function getStatusText(status: string, bankingStatus: string) {
        if (bankingStatus === 'error') {
            return 'Ошибка создания'
        }
        if (status === 'created') {
            return 'Ожидает оплаты'
        }
        if (status === 'payment_uploaded') {
            return 'Подтвержден'
        }
        if (status === 'paid') {
            return 'Оплачен'
        }
        return 'Неизвестно'
    }

    function getStatusColor(status: string, bankingStatus: string) {
        if (bankingStatus === 'error') {
            return '#f44336'
        }
        if (status === 'created') {
            return '#ff9800'
        }
        if (status === 'payment_uploaded') {
            return '#4caf50'
        }
        if (status === 'paid') {
            return '#4caf50'
        }
        return '#757575'
    }

    function getClosingDocumentStatusText(s?: ClosingDocumentStatus) {
        if (s === 'downloaded') return 'Акт скачан'
        if (s === 'emailed') return 'Акт отправлен на почту'
        if (s === 'edo_requested') return 'Ожидает обмена по ЭДО'
        return ''
    }

    function isBillFinalized(bill: Bill) {
        return bill.status === 'payment_uploaded' || bill.status === 'paid'
    }

    return (
        <div className="bills-list">
            { loading && <ListLoader loading={loading} />}
            {!loading && bills.length === 0 && (
                <div className="empty-state">
                    <div className="empty-state-title">Список пуст</div>
                    <div className="empty-state-description">
                        Здесь будут отображаться ваши счета
                    </div>
                </div>
            )}
            {!loading && bills.length > 0 && bills.map((bill) => (
                <div key={bill._id} className="bill-card">
                    <div className="bill-header">
                        <div className="bill-number">
                            Счет №{bill.invoice_number} на {bill.price / 100} ₽
                        </div>
                        <div className="bill-actions-icons">
                            {bill.pdf_url && bill.banking_status !== 'error' && (
                                <button
                                    className="new iconed dark"
                                    onClick={() => downloadBill(bill._id, bill.pdf_url!)}
                                    title="Скачать счет"
                                >
                                    <img src={downloadIcon} alt="Скачать" />
                                </button>
                            )}
                            
                            <button
                                className="new iconed dark"
                                onClick={() => handleDeleteClick(bill._id)}
                                title="Удалить счет"
                            >
                                <img src={trashIcon} alt="Удалить" />
                            </button>
                        </div>
                    </div>
                    
                    <div 
                        className="bill-status"
                        style={{ color: getStatusColor(bill.status, bill.banking_status) }}
                    >
                        {getStatusText(bill.status, bill.banking_status)}
                    </div>

                    {bill.closing_document_status && (
                        <div
                            className="bill-status"
                            style={{ color: '#2196f3', fontSize: 13, marginTop: 2 }}
                        >
                            {getClosingDocumentStatusText(bill.closing_document_status)}
                        </div>
                    )}
                    
                    <div className="bill-description">
                        <div className="bill-description-text">
                            {bill.order_description}
                            {bill.has_additional_services && (
                                <>
                                    <span className="additional-services-text"> + доп. услуги</span>
                                    <InfoButton 
                                        onClick={() => handleShowOrderDetails(bill.items)}
                                        size="small"
                                    />
                                </>
                            )}
                        </div>
                    </div>
                    
                    <div className="bill-date">
                        Создан: {formatDateTime(bill.created_at)}
                    </div>
                    
                    {bill.status === 'created' && bill.banking_status !== 'error' && (
                        <div className="bill-actions">
                            <button
                                className="action-button primary bill-upload-button"
                                onClick={() => uploadPaymentProof(bill._id)}
                            >
                                <span className="button-text">Загрузить платежное поручение</span>
                            </button>
                        </div>
                    )}
                    
                    {isBillFinalized(bill) && (
                        <div className="bill-actions">
                            <button
                                className="action-button secondary bill-upload-button"
                                onClick={() => handleRequestClosingDocs(bill)}
                            >
                                <span className="button-text">Закрывающие документы</span>
                            </button>
                        </div>
                    )}
                </div>
            ))}

            {showDeleteConfirmation && (
                <ConfirmationPopup
                    title="Удалить счет?"
                    subtitle="Это действие нельзя отменить"
                    onClose={() => {
                        setShowDeleteConfirmation(false)
                        setBillToDelete(null)
                    }}
                    onConfirm={() => billToDelete && deleteBill(billToDelete)}
                    confirmText="Удалить"
                    cancelText="Отмена"
                />
            )}

            {showUploadPopup && billToUpload && (
                <UploadPaymentProofPopup
                    $store={$store}
                    $commitToStore={$commitToStore}
                    isOpen={showUploadPopup}
                    onClose={handleUploadClose}
                    billId={billToUpload}
                    onSuccess={handleUploadSuccess}
                    items={bills.find(bill => bill._id === billToUpload)?.items || []}
                />
            )}

            {showOrderDetailsPopup && (
                <Popup onClose={() => setShowOrderDetailsPopup(false)}>
                    <OrderInfo showTitle={true} cart={selectedBillItems} />
                </Popup>
            )}

            {showClosingDocsPopup && selectedBill && (
                <RequestClosingDocsPopup
                    close={() => {
                        setShowClosingDocsPopup(false)
                        setSelectedBill(null)
                    }}
                    $store={$store}
                    $commitToStore={$commitToStore}
                    bill={selectedBill}
                    onSuccess={loadBills}
                />
            )}
        </div>
    )
}

BillsList = connect(
    (store) => ({ $store: store }),
    (dispatch) => ({
        $commitToStore: (data: any) => dispatch({ ...data, type: 'S' })
    })
)(BillsList)

export default BillsList
